import Text from 'components/Text';
import colorTheme from 'constants/color';
import styled from 'styled-components';
import UploadAvatarImage from './components/UploadAvatarImage';
import Button from 'components/Button';
import IcDownload from 'assets/Icons/IcDownload';
import UploadQRImage from './components/UploadQRImage';
import settingStaffSelectors from 'features/settingStaff/services/selectors';
import { useAppDispatch } from 'store/hooks';
import toast from 'utils/toast';
import { getBase64 } from 'utils/getBase64';
import { RcFile } from 'antd/es/upload';
import { IUpdateStaffAvatar } from 'features/settingStaff/services/types/api';
import settingStaffActions from 'features/settingStaff/services/actions';
import settingApis from 'features/settingStaff/services/apis';
import { removeBase64Prefix } from 'utils/removeBase64Prefix';

const StaffImages = () => {
  const dispatch = useAppDispatch();
  const targetStaff = settingStaffSelectors.selectedStaffData.data();

  const handleChangeAvatar = async (info: File) => {
    if (info) {
      try {
        const urlBase64 = await getBase64(info as RcFile);
        const params: IUpdateStaffAvatar = {
          staffId: targetStaff?.id || '',
          avatar: urlBase64 || '',
        };
        dispatch(settingStaffActions.updateStaffAvatar.fetch(params));
      } catch (error) {
        toast.error('Failed to retrieve the new avatar.');
      }
    } else {
      toast.error('File upload failed.');
    }
  };

  const handleDeleteAvatar = () => {
    try {
      const params: IUpdateStaffAvatar = {
        staffId: targetStaff?.id || '',
        avatar: '',
      };
      dispatch(settingStaffActions.updateStaffAvatar.fetch(params));
    } catch (error) {
      toast.error('Failed to delete the avatar.');
    }
  };

  const handleChangeVenmoTipQR = async (file: File) => {
    if (file) {
      try {
        const base64 = await getBase64(file as RcFile);
        const urlBase64 = removeBase64Prefix(base64 || '');
        const res = await settingApis.updateVenmoTipQR(targetStaff?.id || '', urlBase64);

        if (res?.data?.data) {
          toast.success('Venmo tip QR updated successfully.');
          dispatch(settingStaffActions.getSelectedStaffInfo.fetch(targetStaff?.id || ''));
        }
      } catch (error) {
        toast.error('Failed to update the Venmo tip QR.');
      }
    } else {
      toast.error('File upload failed.');
    }
  };

  const handleDeleteVenmoTipQR = async () => {
    try {
      const res = await settingApis.updateVenmoTipQR(targetStaff?.id || '', '');
      if (res?.data?.data) {
        toast.success('Venmo tip QR deleted successfully.');
        dispatch(settingStaffActions.getSelectedStaffInfo.fetch(targetStaff?.id || ''));
      }
    } catch (error) {
      toast.error('Failed to delete the Venmo tip QR.');
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector('.upload-qr-image canvas') as HTMLCanvasElement;

    if (!canvas) {
      toast.error('QR not found');
      return;
    }

    const url = canvas.toDataURL('image/png');

    const link = document.createElement('a');

    link.href = url;
    link.download = 'qr-code.png';

    link.click();
  };

  return (
    <Container>
      <div className='avatar'>
        <Title>
          <Text variant='H7' textTransform='capitalize'>
            AVATAR
          </Text>
        </Title>
        <Content>
          <ImageContainer>
            <UploadAvatarImage
              className='upload-avatar-image'
              value={targetStaff?.urlImage}
              onChange={handleChangeAvatar}
              onDelete={handleDeleteAvatar}
            />
          </ImageContainer>
          <Text variant='CONTENT_1' color='text_4_v2'>
            Size: 512x512px. Max size: 3MB
          </Text>
        </Content>
      </div>
      <div className='qr-venmo-tip'>
        <Title>
          <Text variant='H7' textTransform='capitalize'>
            QR VENMO TIP
          </Text>
        </Title>
        <Content>
          <ImageContainer>
            <UploadQRImage
              className='upload-qr-image'
              value={targetStaff?.urlQrCodeVenmo}
              onChange={handleChangeVenmoTipQR}
              onDelete={handleDeleteVenmoTipQR}
            />
          </ImageContainer>
          <Text variant='CONTENT_1' color='text_4_v2'>
            Size: 512x512px. Max size: 3MB
          </Text>
        </Content>
      </div>
      <div className='qr-code-check-in'>
        <Title>
          <Text variant='H7' textTransform='capitalize'>
            QR CODE CHECK IN
          </Text>
        </Title>
        <Content>
          <ImageContainer>
            <UploadQRImage className='upload-qr-image' value={targetStaff?.qrCodeCheckIn} showQR={true} />
          </ImageContainer>
          <Button ntype='NEW_TYPE' height={3} iconElement={<IcDownload />} width='100%' onClick={handleDownloadQR} disabled={!targetStaff?.qrCodeCheckIn}>
            Download
          </Button>
          <Text variant='CONTENT_1' color='text_4_v2'>
            Size: 512x512px. Max size: 3MB
          </Text>
        </Content>
      </div>
    </Container>
  );
};

export default StaffImages;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  padding: 24px 48px;
  .avatar,
  .qr-venmo-tip,
  .qr-code-check-in {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 56px;
    border-radius: 16px;
    border: 1px solid ${colorTheme.v2_border_1};
    background: ${colorTheme.v2_neutral_0};
    box-shadow: 0 15px 30px -15px rgba(0, 0, 0, 0.15);
  }
`;

const Title = styled.div`
  padding: 24px 24px 16px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;

  .upload-avatar-image,
  .upload-qr-image {
    width: 220px;
    height: 220px;
  }
`;

const Content = styled.div`
  padding: 56px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: center;
`;
